// src/pages/InvitePage.jsx
// Route: /invite/:token — public.
//
// The invitee's side of corporate onboarding. An organisation (an employer)
// gave Previu a roster; this page is where the PERSON decides. Nothing exists
// for them until they do: no account, no consent, no health data.
//
//   intro   who invited you + the exact consent text → "Send me a code"
//   verify  6-digit WhatsApp code to the phone the organisation listed, then
//           either accept (consent + DOB/gender/email) or decline
//
// The code is what makes the choice theirs. Anyone can open a forwarded link; only
// the holder of the listed phone can accept or decline. The consent text shown is
// whatever the server returned, never a copy kept here — the server hashes that
// same text when it records the consent.
//
// Styling: shared index.css classes only (.login-*, .form-*, .alert-*, .invite-*).

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { inviteAPI } from '../services/api';
import { usePatient } from '../context/PatientContext';
import { resolvePostLoginRoute, goToPostLoginRoute } from './PatientLoginPage';
import previuLogo from '../assets/previu-logo.jpeg';

const RESEND_SECONDS = 60;

const ENDED_COPY = {
  ACCEPTED: 'This invitation has already been used. If that was you, you can sign in.',
  DECLINED: 'This invitation was declined, so there is nothing more to do.',
  EXPIRED:  'This invitation has expired. Ask the person who invited you to send a new one.',
  REVOKED:  'This invitation is no longer available.',
};

const today = () => new Date().toISOString().slice(0, 10);
const message = (e, fallback) => e?.response?.data?.message || fallback;

export default function InvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { refreshPatient } = usePatient();

  const [phase, setPhase]     = useState('loading'); // loading | invalid | ended | intro | verify | declined
  const [invite, setInvite]   = useState(null);
  const [error, setError]     = useState('');
  const [busy, setBusy]       = useState(false);
  const [resendIn, setResendIn] = useState(0);

  const [otp, setOtp]         = useState('');
  const [dob, setDob]         = useState('');
  const [gender, setGender]   = useState('');
  const [email, setEmail]     = useState('');
  const [agreed, setAgreed]   = useState(false);

  useEffect(() => {
    let alive = true;
    inviteAPI.view(token)
      .then(res => {
        if (!alive) return;
        setInvite(res.data);
        setPhase(res.data.status === 'PENDING' ? 'intro' : 'ended');
      })
      .catch(() => alive && setPhase('invalid'));
    return () => { alive = false; };
  }, [token]);

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const t = setTimeout(() => setResendIn(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  async function sendCode() {
    setError('');
    setBusy(true);
    try {
      await inviteAPI.sendOtp(token);
      setPhase('verify');
      setResendIn(RESEND_SECONDS);
    } catch (e) {
      // 429 COOLDOWN still means a code is on its way — go to the entry step.
      if (e.response?.data?.error === 'COOLDOWN') {
        setPhase('verify');
        setResendIn(RESEND_SECONDS);
      } else {
        setError(message(e, 'We could not send the code. Please try again.'));
      }
    } finally {
      setBusy(false);
    }
  }

  const codeOk = /^[0-9]{6}$/.test(otp);

  async function accept(e) {
    e.preventDefault();
    setError('');
    if (!codeOk) { setError('Enter the 6-digit code we sent you on WhatsApp.'); return; }
    if (!agreed) { setError('Please tick the box to confirm you have read and accept the consent.'); return; }
    setBusy(true);
    try {
      const { data } = await inviteAPI.accept(token, {
        otp, consentGiven: true, dateOfBirth: dob, gender, email: email.trim() || null,
      });
      localStorage.setItem('previu_token', data.token);
      const nameParts = (data.name || '').trim().split(' ');
      localStorage.setItem('previu_patient', JSON.stringify({
        id:        data.patientId,
        firstName: nameParts[0] || 'Patient',
        lastName:  nameParts.slice(1).join(' ') || '',
        phone:     data.phone,
        email:     data.email || '',
      }));
      refreshPatient();
      goToPostLoginRoute(navigate, await resolvePostLoginRoute(data.patientId));
    } catch (err) {
      if (err.response?.status === 410) setPhase('ended');
      setError(message(err, 'Something went wrong. Please try again.'));
      setBusy(false);
    }
  }

  async function decline() {
    setError('');
    if (!codeOk) { setError('Enter the 6-digit code first — it confirms the choice is yours.'); return; }
    setBusy(true);
    try {
      await inviteAPI.decline(token, otp);
      setPhase('declined');
    } catch (err) {
      setError(message(err, 'Something went wrong. Please try again.'));
    } finally {
      setBusy(false);
    }
  }

  function renderBody() {
    if (phase === 'loading') {
      return <div className="skeleton skeleton--body" aria-busy="true" />;
    }
    if (phase === 'invalid') {
      return (
        <>
          <div className="login-title">Link not recognised</div>
          <div className="login-sub">
            This invitation link is not valid. Check you copied the whole link, or ask the
            person who invited you to send it again.
          </div>
        </>
      );
    }
    if (phase === 'ended') {
      const status = invite?.status;
      return (
        <>
          <div className="login-title">Invitation unavailable</div>
          <div className="login-sub">{ENDED_COPY[status] || ENDED_COPY.REVOKED}</div>
          {status === 'ACCEPTED' && (
            <Link className="login-btn" to="/login" style={{ display: 'block', textAlign: 'center', textDecoration: 'none' }}>
              Sign in
            </Link>
          )}
        </>
      );
    }
    if (phase === 'declined') {
      return (
        <>
          <div className="login-title">No problem</div>
          <div className="login-sub">
            You have declined, and nothing has been created for you. If you change your mind
            you can ask for a new invitation, or register yourself at any time.
          </div>
        </>
      );
    }

    const greeting = invite.firstName ? `Hi ${invite.firstName},` : 'Hello,';
    return (
      <>
        <div className="login-title">{greeting}</div>
        <div className="login-sub">
          {invite.organisationName} has invited you to Previu Health. Joining is your choice.
          Read what you would be agreeing to, then confirm it is you with a code sent to
          WhatsApp number {invite.maskedPhone}.
        </div>

        {error && <div className="alert alert-error" role="alert">{error}</div>}

        <div className="invite-consent" tabIndex={0} aria-label="Consent text">
          {invite.consentText}
        </div>

        {phase === 'intro' && (
          <button type="button" className="login-btn" disabled={busy} onClick={sendCode}>
            {busy ? 'Sending…' : 'Send me a code'}
          </button>
        )}

        {phase === 'verify' && (
          <form onSubmit={accept} noValidate>
            <div className="form-group">
              <label className="form-label" htmlFor="invite-otp">6-digit code</label>
              <input id="invite-otp" className="login-input" inputMode="numeric" autoComplete="one-time-code"
                maxLength={6} value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, ''))} />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="invite-dob">Date of birth</label>
              <input id="invite-dob" className="login-input" type="date" max={today()} min="1900-01-01"
                value={dob} onChange={e => setDob(e.target.value)} required />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="invite-gender">Gender</label>
              <select id="invite-gender" className="login-input" value={gender}
                onChange={e => setGender(e.target.value)} required>
                <option value="" disabled>Select…</option>
                <option value="FEMALE">Female</option>
                <option value="MALE">Male</option>
                <option value="OTHER">Other</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="invite-email">Email</label>
              <input id="invite-email" className="login-input" type="email" autoComplete="email"
                value={email} onChange={e => setEmail(e.target.value)} required />
              <div className="form-hint">Use a personal address. {invite.organisationName} is not told what you enter here.</div>
            </div>

            <label className="invite-check">
              <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} />
              <span>I have read the consent above and I accept it.</span>
            </label>

            <div className="invite-actions">
              <button type="submit" className="login-btn"
                disabled={busy || !agreed || !codeOk || !dob || !gender || !email.trim()}>
                {busy ? 'Please wait…' : 'Accept and join Previu'}
              </button>
              <button type="button" className="btn btn-ghost btn-sm" disabled={busy || resendIn > 0} onClick={sendCode}>
                {resendIn > 0 ? `Resend code in ${resendIn}s` : 'Resend code'}
              </button>
              <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={decline}>
                No thanks
              </button>
            </div>
          </form>
        )}
      </>
    );
  }

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-logo-area">
          <img src={previuLogo} alt="Previu" style={{ height: 96, width: 'auto' }} />
        </div>
        {renderBody()}
      </div>
    </div>
  );
}
